import React, { useEffect, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router-dom";

import Header from "../../components/Header";
import Footer from "../../components/Footer";
import api from "../../services/api";
import DocumentsModal from "./components/DocumentModal";

import {
  Container,
  Content,
  PageTitle,
  List,
  Card,
  Photo,
  Info,
  Title,
  Address,
  MetaRow,
  Meta,
  Status,
  Actions,
  Button,
  EmptyState,
  SkeletonText,
  HeaderLayer,
  FooterLayer,
} from "./styles";

import { resolveRentProcessStatus } from "./utils/rentProcessStatus";
import { useAuth } from "../../hooks/auth";

type RentProcess = {
  id: number;
  status: string;
  property_id: number;
  title: string;
  rent_value: string;
  condominium_value: string;
  iptu_value: string;
  address: string;
  neighborhood: string;
  city: string;
  uf: string;
  photo: string | null;
};

type LocationState = {
  rentProcessId?: number;
};

type Props = RouteComponentProps<{}, any, LocationState>;

export default function UserDocuments(props: Props) {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [processes, setProcesses] = useState<RentProcess[]>([]);
  const [selectedRentProcessId, setSelectedRentProcessId] = useState<number | null>(null);

  // ✅ rentProcessId vindo de:
  // 1) navigation state (history.push(..., { rentProcessId }))
  // 2) query param (link compartilhável): /user-documents?rentProcessId=123
  const searchParams = useMemo(() => new URLSearchParams(props.location.search), [props.location.search]);
  const rentProcessIdFromQuery = searchParams.get("rentProcessId");

  const initialRentProcessId =
    props.location.state?.rentProcessId ??
    (rentProcessIdFromQuery ? Number(rentProcessIdFromQuery) : null);

  // ✅ auth guard SOMENTE nessa rota
  useEffect(() => {
    if (!user) {
      const redirectTo = props.location.pathname + props.location.search;
      props.history.replace(`/login?redirect=${encodeURIComponent(redirectTo)}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, props.location.pathname, props.location.search]);

  useEffect(() => {
    async function loadRentProcesses() {
      try {
        const res = await api.get("/api/client/tenant/rent-process");
        setProcesses(res.data.data ?? []);
      } finally {
        setLoading(false);
      }
    }

    // só carrega se logado (evita request desnecessário)
    if (user) loadRentProcesses();
  }, [user]);

  useEffect(() => {
    if (initialRentProcessId) {
      setSelectedRentProcessId(initialRentProcessId);

      // ✅ opcional (recomendado):
      // se veio por navigation state, pode limpar pra não reabrir ao voltar
      // (não mexe em query string compartilhável)
      // props.history.replace(props.location.pathname + props.location.search, {} as LocationState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRentProcessId]);

  const selectedProcess = useMemo(
    () => processes.find((p) => p.id === selectedRentProcessId) ?? null,
    [processes, selectedRentProcessId]
  );

  return (
    <Container>
      <HeaderLayer>
        <Header hasBorder={true} hasSearchBar hasOptionToSearch />
      </HeaderLayer>

      <Content>
        <PageTitle>
          <h2>Documentação de locação</h2>
          <p>Escolha um processo para visualizar, baixar, enviar ou excluir seus documentos.</p>
        </PageTitle>

        {loading && (
          <List>
            {[1, 2, 3].map((n) => (
              <Card key={n}>
                <Photo src={"/placeholder.png"} alt="" />
                <Info>
                  <SkeletonText style={{ width: "55%" }} />
                  <div style={{ height: 8 }} />
                  <SkeletonText style={{ width: "75%" }} />
                  <div style={{ height: 10 }} />
                  <SkeletonText style={{ width: "65%" }} />
                </Info>
                <Actions>
                  <Button disabled>Carregando...</Button>
                </Actions>
              </Card>
            ))}
          </List>
        )}

        {!loading && processes.length === 0 && (
          <EmptyState>
            <p>Nenhum processo de locação encontrado.</p>
          </EmptyState>
        )}

        {!loading && processes.length > 0 && (
          <List>
            {processes.map((p) => (
              <Card key={p.id} onClick={() => setSelectedRentProcessId(p.id)}>
                <Photo src={p.photo ?? "/placeholder.png"} alt={p.title} />

                <Info>
                  <Title title={p.title}>{p.title}</Title>
                  <Address title={`${p.address}, ${p.neighborhood} - ${p.city}/${p.uf}`}>
                    {p.address}, {p.neighborhood} - {p.city}/{p.uf}
                  </Address>

                  <MetaRow>
                    <Meta>Aluguel: R$ {p.rent_value}</Meta>
                    <Meta>Condo: R$ {p.condominium_value}</Meta>
                    <Meta>IPTU: R$ {p.iptu_value}</Meta>

                    {(() => {
                      const ui = resolveRentProcessStatus(p.status);
                      return <Status variant={ui.variant}>{ui.label}</Status>;
                    })()}
                  </MetaRow>
                </Info>

                <Actions>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedRentProcessId(p.id);
                    }}
                  >
                    Ver documentos
                  </Button>
                </Actions>
              </Card>
            ))}
          </List>
        )}
      </Content>

      {selectedRentProcessId && (
        <DocumentsModal
          propertyId={selectedProcess?.property_id ?? 0}
          rentProcessId={selectedRentProcessId}
          title={selectedProcess?.title}
          onClose={() => setSelectedRentProcessId(null)}
        />
      )}

      <FooterLayer>
        <Footer />
      </FooterLayer>
    </Container>
  );
}
